import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";

const WHATSAPP_NUMBER = "918097025643";
const AGENT_NAME = "Birbal";
const AGENT_ROLE = "Customer Support";
const WELCOME_MSG = "Hello! How can we help you?";
const DEFAULT_USER_MSG = "Hello! I have a question.";
const AGENT_AVATAR = "https://static.wixstatic.com/media/97afd0_b00add3243fe419798eca110b9dee25~mv2.jpg/v1/fill/w_240,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_auto/97afd0_b00add3243fe419798eca110b9dee25~mv2.jpg";

export default function WhatsAppWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [userMsg, setUserMsg] = useState(DEFAULT_USER_MSG);

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const openWhatsApp = () => {
    const encoded = encodeURIComponent(userMsg);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, "_blank");
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100000] flex flex-col items-end gap-3" data-testid="whatsapp-widget">
      {/* Welcome bubble */}
      <AnimatePresence>
        {showBubble && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-xl px-4 py-3 max-w-[220px] relative mr-1"
          >
            <button
              onClick={() => setShowBubble(false)}
              className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs hover:bg-slate-300 transition-colors"
              data-testid="whatsapp-bubble-close"
            >
              <X size={10} />
            </button>
            <p className="text-sm text-slate-700 font-medium">{WELCOME_MSG}</p>
            <div className="absolute bottom-[-6px] right-5 w-3 h-3 bg-white rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="w-[320px] rounded-2xl overflow-hidden shadow-2xl"
            data-testid="whatsapp-chat-panel"
          >
            {/* Header */}
            <div className="bg-[#008069] px-5 py-4 flex items-center justify-between">
              <div>
                <h4 className="text-white font-semibold text-base">Hello!</h4>
                <p className="text-[#d1f4e0] text-xs mt-0.5 leading-relaxed">
                  We are happy to help you with any questions. Click below to send a message.
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors ml-3 flex-shrink-0"
                data-testid="whatsapp-panel-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Chat background */}
            <div
              className="px-4 py-5 min-h-[140px]"
              style={{
                backgroundColor: "#E5DDD5",
                backgroundImage: "url('https://mkp-prod.nyc3.cdn.digitaloceanspaces.com/whatsapp-chat/assets/image_whatsapp-chat-background-classic.png')",
                backgroundSize: "cover",
              }}
            >
              {/* Agent card */}
              <button
                onClick={openWhatsApp}
                className="w-full bg-white rounded-xl p-3 flex items-center gap-3 hover:shadow-md transition-shadow text-left group"
                data-testid="whatsapp-agent-card"
              >
                <div className="relative flex-shrink-0">
                  <img
                    src={AGENT_AVATAR}
                    alt={AGENT_NAME}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#24D366] rounded-full border-2 border-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{AGENT_NAME}</p>
                  <p className="text-xs text-slate-500">{AGENT_ROLE}</p>
                  <p className="text-xs text-[#24D366] mt-0.5">I'm here to help!</p>
                </div>
                <svg className="w-5 h-5 text-slate-400 group-hover:text-[#24D366] transition-colors flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Input area */}
            <div className="bg-[#f8f9fa] px-4 py-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={userMsg}
                  onChange={(e) => setUserMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && openWhatsApp()}
                  placeholder="Type a message..."
                  className="flex-1 bg-white rounded-full px-4 py-2.5 text-sm text-slate-700 border border-slate-200 focus:border-[#24D366] focus:ring-1 focus:ring-[#24D366] outline-none transition-all"
                  data-testid="whatsapp-message-input"
                />
                <button
                  onClick={openWhatsApp}
                  className="w-10 h-10 bg-[#24D366] rounded-full flex items-center justify-center hover:bg-[#20bd5a] transition-colors flex-shrink-0"
                  data-testid="whatsapp-send-button"
                >
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-2">Feel free to reach out to us anytime!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen(!isOpen);
          setShowBubble(false);
        }}
        className="w-14 h-14 rounded-full bg-[#24D366] shadow-lg flex items-center justify-center hover:bg-[#20bd5a] transition-colors relative"
        data-testid="whatsapp-toggle-button"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <svg className="w-7 h-7 text-white" viewBox="0 0 32 32" fill="currentColor">
            <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.31 22.606c-.39 1.1-1.932 2.014-3.164 2.28-.844.18-1.946.322-5.656-1.216-4.748-1.966-7.804-6.78-8.038-7.094-.226-.314-1.886-2.512-1.886-4.794 0-2.28 1.194-3.402 1.616-3.868.39-.432.918-.604 1.218-.604.15 0 .284.008.404.014.39.018.586.042.844.65.322.762 1.108 2.704 1.206 2.902.1.198.194.462.064.744-.122.288-.228.466-.448.714-.218.248-.46.554-.656.744-.218.216-.446.452-.192.886.254.432 1.13 1.862 2.426 3.018 1.666 1.488 3.07 1.95 3.506 2.166.434.216.688.182.942-.11.254-.294 1.09-1.268 1.38-1.7.288-.434.578-.36.972-.216.396.144 2.506 1.182 2.938 1.396.434.216.722.322.828.502.104.178.104 1.04-.286 2.142z"/>
          </svg>
        )}
        {/* Notification dot */}
        {!isOpen && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
            <span className="text-[8px] text-white font-bold">1</span>
          </span>
        )}
      </motion.button>
    </div>
  );
}
